import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, Mail } from 'lucide-react';

const AuthPage = () => {
  const { login } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'accountant',
  });
  const [loading, setLoading] = useState(false);
  const [twofaRequired, setTwofaRequired] = useState(false);
  const [twofaCode, setTwofaCode] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState(null);
  const [magicLoading, setMagicLoading] = useState(false);

  // Magic-link login via invite email: /auth?token=XYZ&flow=invite
  useEffect(() => {
    const token = searchParams.get('token');
    const flow = searchParams.get('flow');
    if (!token || flow !== 'invite' || magicLoading) return;
    setMagicLoading(true);
    (async () => {
      try {
        const r = await axios.post(`${API}/auth/magic-login`, { token });
        login(r.data.access_token, r.data.user);
        toast.success(`Welcome ${r.data.user.name}! You've been signed in.`);
        // Clear sensitive params from URL
        setSearchParams({});
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Invite link expired. Please sign in manually.');
        setSearchParams({});
      } finally {
        setMagicLoading(false);
      }
    })();
  }, [searchParams, login, setSearchParams, magicLoading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!isLogin) {
        // Magic-link registration: backend ignores any password we send.
        const r = await axios.post(`${API}/auth/register`, {
          email: formData.email,
          name: formData.name,
          role: formData.role,
        });
        if (r.data?.registered) {
          setRegisteredEmail(r.data.email || formData.email);
          toast.success('Account created — check your email to finish setup.');
        }
        return;
      }
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password,
        twofa_code: twofaCode || undefined,
      });
      if (response.data?.twofa_required) {
        setTwofaRequired(true);
        toast.info('Enter your 6-digit code from your authenticator app');
        return;
      }
      login(response.data.access_token, response.data.user);
      toast.success(`Welcome ${response.data.user.name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    const redirect = encodeURIComponent(window.location.origin);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
  };

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1666618207644-4de0226a3f85?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Office workspace"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/20" />
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="mb-8">
            <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="auth-title">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h1>
            <p className="text-slate-600 mt-2 font-ibm">
              {isLogin ? 'Sign in to manage your finances' : 'Start managing your company finances'}
            </p>
          </div>

          {registeredEmail && !isLogin && (
            <div
              className="mb-4 rounded-sm border border-emerald-200 bg-emerald-50 p-4"
              data-testid="register-success-banner"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-ibm font-semibold text-sm text-slate-900">
                    Check your inbox
                  </div>
                  <p className="text-xs font-ibm text-slate-700 mt-1">
                    We&apos;ve sent a setup link to{' '}
                    <strong className="text-slate-900">{registeredEmail}</strong>. Open it within
                    24 hours to set your password and finish creating your account.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="auth-form">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                  Full Name
                </Label>
                <Input
                  id="name"
                  data-testid="input-name"
                  type="text"
                  required={!isLogin}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                  placeholder="John Doe"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                Email Address
              </Label>
              <Input
                id="email"
                data-testid="input-email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                placeholder="john@company.com"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                Password
              </Label>
              <Input
                id="password"
                data-testid="input-password"
                type="password"
                required={isLogin}
                disabled={!isLogin}
                value={isLogin ? formData.password : ''}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className={`mt-2 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm ${
                  !isLogin ? 'bg-slate-100 cursor-not-allowed' : 'bg-slate-50'
                }`}
                placeholder={isLogin ? '••••••••' : "You'll set this from your welcome email"}
              />
              {!isLogin && (
                <p className="mt-1.5 text-[11px] font-ibm text-slate-500">
                  For security, we email a one-time setup link instead of accepting a password here.
                </p>
              )}
              {isLogin && (
                <div className="mt-1.5 text-right">
                  <Link
                    to="/auth/forgot"
                    data-testid="forgot-password-link"
                    className="text-[11px] font-ibm text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>

            {!isLogin && (
              <div>
                <Label htmlFor="role" className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                  User Role
                </Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="select-role" className="mt-2 bg-slate-50 border-slate-200 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="accountant">Accountant</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {isLogin && twofaRequired && (
              <div>
                <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                  2FA Code
                </Label>
                <Input
                  data-testid="input-2fa-code"
                  type="text"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  required
                  value={twofaCode}
                  onChange={(e) => setTwofaCode(e.target.value)}
                  placeholder="6-digit code or recovery code"
                  className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm tracking-widest text-center"
                />
              </div>
            )}

            <Button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-12 font-ibm font-semibold hover:scale-[0.98] transition-transform"
            >
              {(() => {
                if (loading) return 'Processing...';
                return isLogin ? 'Sign In' : 'Create Account';
              })()}
            </Button>
          </form>

          {isLogin && (
            <div className="mt-4">
              <div className="relative my-4 text-center">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                <span className="relative bg-white px-3 text-xs font-ibm uppercase tracking-widest text-slate-500">
                  Or
                </span>
              </div>
              <Button
                type="button"
                onClick={handleGoogleSignIn}
                data-testid="google-signin-button"
                className="w-full bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm h-12 font-ibm font-semibold hover:scale-[0.98] transition-transform"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" className="mr-2">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-mode"
              className="text-slate-600 hover:text-slate-900 font-ibm"
            >
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <span className="font-semibold">{isLogin ? 'Sign Up' : 'Sign In'}</span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;